/**
 * gen_623.js — Placer une fraction sur une demi-droite graduée
 * Cluster 12, Attendu 623 — 8 générateurs
 */

import { shuffle, randInt, randomChoice, makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier } from '../../../utils';
import { gcd } from './fraction.js';

// ─── Helpers ──────────────────────────────────────────────────

const OPT = { niveau: '6eme', matiere: 'maths', attendu: 623 };

function pickB(pool) { return randomChoice(pool || [2, 3, 4, 5, 6]); }

function fracStr(a, b) { return `$\\dfrac{${a}}{${b}}$`; }

// ─── T1: QCM quel point correspond à a/b ──────────────────────

function placer_fraction_qcm() {
  let a, b;
  do {
    b = pickB();
    a = randInt(1, 3 * b);
  } while (a === b); // avoid a/b = 1 where b/a = 1 too

  const end = Math.ceil(a / b) + 1;
  const correct = a / b;
  // leurres: b/a, (a-1)/b or (a+1)/b, a/(b+1) or a/(b-1)
  // Chaque leurre doit TENIR sur la droite (0 < v ≤ end), comme le fallback :
  // sinon le composant ne dessine pas le point et une lettre du QCM reste
  // orpheline (13 % des tirages — b/a explose quand a est petit, revue 2026-08-02).
  const leurres = [];
  const ajouterLeurre = (v) => {
    if (v !== correct && v > 0 && v <= end && !leurres.includes(v)) leurres.push(v);
  };
  ajouterLeurre(b / a);
  ajouterLeurre(a > 1 ? (a - 1) / b : (a + 1) / b);
  ajouterLeurre(b > 2 ? a / (b - 1) : a / (b + 1));
  // fallback
  while (leurres.length < 3) {
    const v = (a + randInt(1, 3)) / b;
    if (v !== correct && !leurres.includes(v) && v > 0 && v <= end) leurres.push(v);
  }
  const positions = [correct, ...leurres.slice(0, 3)];

  // assign labels A-D
  const labels = ['A', 'B', 'C', 'D'];
  const shuffledIdx = shuffle([0, 1, 2, 3]);
  const points = shuffledIdx.map((origIdx, i) => ({
    value: positions[origIdx],
    label: labels[i],
    color: '#fb923c',
  }));
  // correct label
  const correctLabel = labels[shuffledIdx.indexOf(0)];

  // Entiers tous étiquetés (demande utilisateur 2026-08-02) : 0 - 1 - 2…
  const reperes = [];
  for (let e = 1; e < end; e++) reperes.push(e);
  const nlConfig = { start: 0, end: end, step: 1 / b, points, showStart: true, showEnd: true, reperes };
  const texte = `<p>Observe la demi-droite graduée. Quel point correspond à la fraction {{element:1}} ?</p>{{element:0}}`;

  const choix = labels.slice();
  return {
    ...makeQcm(choix, correctLabel, texte, { ...OPT, label: 'Identifier la position d\'une fraction sur une droite graduée' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }, { type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}}` } }] },
    _meta: { generatorKey: 'placer_fraction_qcm' }
  };
}

// ─── T2: placer_points interactif ─────────────────────────────

function placer_fraction_interactif() {
  const b = randomChoice([2, 3, 4, 5, 6, 8, 10]);
  const a = randInt(1, 3 * b);
  const end = Math.ceil(a / b) + 1;
  const val = a / b;
  // bonne_reponse produite par le MÊME pipeline de snap que le composant élève
  // (NumberLineInteractiveEleve) : round(v/step)×step, clamp, arrondi 3 décimales,
  // String canonique virgule FR. toFixed(6) donnait "0,333333" alors que l'élève
  // ne peut émettre que "0,333" → point inatteignable.
  const step = 1 / b;
  const snappe = Math.max(0, Math.min(end, Math.round(Math.round(val / step) * step * 1000) / 1000));
  const valFr = String(snappe).replace('.', ',');

  return {
    type: 'association',
    texte: `<p>Place le point A d'abscisse {{element:0}} sur la demi-droite graduée.</p>`,
    contenu: {
      subType: 'placer_points',
      // Entiers tous étiquetés (cohérence avec le QCM, 2026-08-02)
      numberline: { start: 0, end, step: 1 / b, reperes: Array.from({ length: Math.max(0, end - 1) }, (_, k) => k + 1) },
      pointsToPlace: ['A'],
      bonne_reponse: JSON.stringify({ A: valFr })
    },
    elements_additionnels: { version: 1, elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}}` } }] },
    niveau: '6eme', matiere: 'maths', attendu: 623,
    _meta: { generatorKey: 'placer_fraction_interactif' }
  };
}

// ─── T3: Relier fractions → points ────────────────────────────

function placer_fraction_relier() {
  const b = randomChoice([3, 4, 5, 6]);
  const pool = [];
  for (let i = 1; i <= 3 * b; i++) pool.push(i);
  const chosen = shuffle(pool).slice(0, 4).sort((x, y) => x - y);
  const end = Math.ceil(chosen[3] / b) + 1;
  // Lettres MÉLANGÉES sur les positions (revue 2026-08-02) : avec A,B,C,D
  // distribuées dans l'ordre croissant, « trier les numérateurs → répondre
  // alphabétiquement » gagnait sans jamais lire la droite (3000/3000 mesuré).
  const labs = shuffle(['A', 'B', 'C', 'D']);

  const points = chosen.map((a, i) => ({ value: a / b, label: labs[i], color: '#fb923c' }));
  // Entiers tous étiquetés (cohérence avec le QCM, 2026-08-02)
  const reperes = [];
  for (let e = 1; e < end; e++) reperes.push(e);
  const nlConfig = { start: 0, end: end, step: 1 / b, points, showStart: true, showEnd: true, reperes };

  const pairs = chosen.map((a, i) => ({ source: fracStr(a, b), target: labs[i] }));
  const texte = `<p>Relie chaque fraction au point correspondant sur la demi-droite graduée.</p>{{element:0}}`;

  return {
    ...makeRelier(pairs, texte, { ...OPT, label: 'Relier fractions et points sur la droite' }),
    elements_additionnels: { version: 1, elements: [{ type: 'numberline', config: nlConfig }] },
    _meta: { generatorKey: 'placer_fraction_relier' }
  };
}

// ─── T4: V/F encadrement ──────────────────────────────────────

function placer_fraction_vf() {
  const lines = [];
  const answers = [];
  const fractions = []; // garder a, b pour construire les numberlines

  const seenFracs = new Set();
  for (let i = 0; i < 3; i++) {
    let b, a, q;
    // Garantir fraction distincte des précédentes (évite dédup en 2 lignes)
    do {
      b = randomChoice([3, 4, 5, 6]);
      a = randInt(b + 1, 4 * b);
    } while (a % b === 0 || seenFracs.has(`${a}/${b}`));
    seenFracs.add(`${a}/${b}`);
    q = Math.floor(a / b);
    fractions.push({ a, b, q });

    if (i < 2) {
      lines.push(`${fracStr(a, b)} se situe entre ${q} et ${q + 1}`);
      answers.push(0);
    } else {
      const wrongQ = q + randomChoice([-1, 1]);
      if (wrongQ < 0) {
        lines.push(`${fracStr(a, b)} se situe entre 0 et 1`);
      } else {
        lines.push(`${fracStr(a, b)} se situe entre ${wrongQ} et ${wrongQ + 1}`);
      }
      answers.push(1);
    }
  }

  const shuffled = shuffle([0, 1, 2]);

  // 3 droites graduées (une par fraction) — sans points, l'élève doit placer
  // mentalement. end COMMUN aux trois (revue 2026-08-02) : mêmes unités aux
  // mêmes pixels, l'élève balaie verticalement pour comparer — seuls les
  // partages 1/b diffèrent d'une droite à l'autre.
  const endCommun = Math.max(...fractions.map((f) => f.q)) + 2;
  const reperes = [];
  for (let k = 1; k < endCommun; k++) reperes.push(k);
  const nlElements = shuffled.map(i => {
    const { b } = fractions[i];
    return { type: 'numberline', config: { start: 0, end: endCommun, step: 1 / b, points: [], reperes } };
  });

  const texte = `<p>Aide-toi des demi-droites graduées ci-dessous pour dire si chaque affirmation est vraie ou fausse.</p>{{element:0}}{{element:1}}{{element:2}}`;

  const payload = makeVraiFaux(
    shuffled.map(i => lines[i]),
    shuffled.map(i => answers[i]),
    texte,
    { ...OPT, label: 'Vrai/Faux sur l\'encadrement par entiers' }
  );
  // Ajouter les 3 numberlines aux éléments existants (le tableau V/F est le dernier)
  const existingElements = payload.elements_additionnels?.elements || [];
  payload.elements_additionnels = {
    version: 1,
    elements: [...nlElements, ...existingElements],
  };
  payload._meta = { generatorKey: 'placer_fraction_vf' };
  return payload;
}

// ─── T5: Texte à trous drag (méthode) ────────────────────────

function placer_methode_drag() {
  let a, b;
  do {
    // Pool élargi (5 → 9 dénominateurs) × plage a étendue → ~150 couples
    b = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10]);
    a = randInt(1, 3 * b);
  } while (a === b - 1 || a === b + 1); // avoid distractor = bonne réponse

  // Fraction en vraie écriture (revue 2026-08-02) : « 7/3 » à plat banni.
  // 3 lignes math courtes : KaTeX display ne se coupe jamais seul (mobile).
  // a = b (deux jetons jumeaux) est SAIN : le drag consomme les étiquettes
  // une par une et la correction juge trou par trou (vérifié).
  const phrase = `$\\text{Pour placer } \\dfrac{${a}}{${b}} \\text{, on divise chaque}$\n$\\text{unité en } {{${b}}} \\text{ parts égales, puis on}$\n$\\text{avance de } {{${a}}} \\text{ parts depuis 0.}$`;

  // distracteurs: pick 2 from {b-1, b+1, a-1, a+1} minus collisions
  const candidates = [b - 1, b + 1, a - 1, a + 1].filter(v => v > 0 && v !== a && v !== b);
  const unique = [...new Set(candidates)];
  const dist = shuffle(unique).slice(0, 2);
  while (dist.length < 2) dist.push(a * b);

  const texte = `<p>Complète la méthode pour placer {{element:0}} sur la demi-droite graduée.</p>`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'drag', distracteurs: dist.map(String), label: 'Méthode de placement d\'une fraction (drag)' }),
    elements_additionnels: { version: 1, elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}}` } }] },
    _meta: { generatorKey: 'placer_methode_drag' }
  };
}

// ─── T6: Saisie encadrement par entiers ───────────────────────

function placer_encadrement_saisie() {
  let a, b;
  do {
    b = randomChoice([3, 4, 5, 6, 7, 8]);
    a = randInt(b + 1, 4 * b);
  } while (a % b === 0);

  const q = Math.floor(a / b);
  const texte = `<p>Entre quels entiers consécutifs se situe {{element:0}} ? Écris le plus petit.</p>`;

  return {
    ...makeSaisie(String(q), texte, { ...OPT, strict: true, label: 'Encadrement d\'une fraction par entiers consécutifs' }),
    elements_additionnels: { version: 1, elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}}` } }] },
    _meta: { generatorKey: 'placer_encadrement_saisie' }
  };
}

// ─── T7: Texte à trous saisie (décomposition) ────────────────

function placer_decomposition_saisie() {
  let a, b;
  do {
    // Pool élargi (4 → 8 dénominateurs) × plage a étendue → ~150 couples
    b = randomChoice([3, 4, 5, 6, 7, 8, 9, 10]);
    a = randInt(b + 1, 4 * b);
  } while (a % b === 0);

  const q = Math.floor(a / b);
  const r = a % b;

  // Étais \rule (revue 2026-08-02, même fix que 620/621/652) : sans eux la
  // barre de fraction disparaît sous les champs de saisie ; \; aère le +
  const phrase = `$\\dfrac{${a}}{${b}} = {{${q}}} \\;+\\; \\dfrac{{{${r}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}$\n$\\dfrac{${a}}{${b}} \\text{ se situe à } {{${r}}} \\text{ graduations après l'entier } {{${q}}}.$`;
  const texte = `<p>Complète pour placer {{element:0}} sur la demi-droite.</p>`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'saisie', distracteurs: [], label: 'Décomposition d\'une fraction pour placer (saisie)' }),
    elements_additionnels: { version: 1, elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a}}{${b}}` } }] },
    _meta: { generatorKey: 'placer_decomposition_saisie' }
  };
}

// ─── T8: QCM dénominateurs mélangés ──────────────────────────

function placer_den_melanges_qcm() {
  // Resserrage (revue 2026-08-02) : fractions < 1 et PPCM ≤ 10 → la droite
  // tient en 2 × PPCM ≤ 20 graduations (l'ancien montait à 60, illisible,
  // et (3,5)/(4,5) imposaient des conversions ×5 hors de portée 6e).
  // Le but de la carte : voir qu'une MÊME droite porte plusieurs partages —
  // couples emboîtés ((2,4), (4,8)…) en entrée de gamme, vrais PPCM
  // ((2,3), (2,5)) au sommet.
  const paires = [[2, 4], [2, 3], [2, 6], [3, 6], [2, 8], [4, 8], [3, 9], [2, 5], [2, 10], [5, 10]];

  let a1, b1, a2, b2, lcm, pos1, pos2, end;
  do {
    [b1, b2] = randomChoice(paires);
    lcm = (b1 * b2) / gcd(b1, b2);
    a1 = randInt(1, b1 - 1);
    do { a2 = randInt(1, b2 - 1); } while (a1 / b1 === a2 / b2);
    pos1 = a1 * (lcm / b1);
    pos2 = a2 * (lcm / b2);
    end = Math.ceil(Math.max(pos1, pos2) / lcm) + 1;
  } while (pos1 === pos2);

  // Bonne réponse et distracteurs en format "trait N et trait M"
  const fmt = (p1, p2) => `$\\dfrac{${a1}}{${b1}}$ au ${p1}ᵉ trait, $\\dfrac{${a2}}{${b2}}$ au ${p2}ᵉ trait`;
  const correctStr = fmt(pos1, pos2);

  const leurreSet = new Set();
  leurreSet.add(fmt(pos1 + 1, pos2));       // off-by-one sur la 1ère
  leurreSet.add(fmt(pos1, pos2 + 1));       // off-by-one sur la 2ème
  leurreSet.add(fmt(pos2, pos1));            // positions inversées
  const confPos = a1 * (lcm / b2);          // confusion dénominateur
  if (confPos !== pos1) leurreSet.add(fmt(confPos, pos2));
  if (pos1 > 1) leurreSet.add(fmt(pos1 - 1, pos2 - 1));

  leurreSet.delete(correctStr);
  const leurres = shuffle([...leurreSet]).slice(0, 3);
  const choix = [correctStr, ...leurres];

  // Droite graduée en PPCM-ièmes (sans points — l'élève compte les traits)
  // reperes = entiers intermédiaires pour forcer l'affichage des labels 0, 1, 2...
  const reperes = [];
  for (let i = 1; i < end; i++) reperes.push(i);
  const nlConfig = { start: 0, end, step: 1 / lcm, points: [], reperes };

  const texte = `<p>Observe la demi-droite graduée en ${lcm}-ièmes ci-dessous. À quels traits se trouvent {{element:1}} et {{element:2}}\u00a0?</p>{{element:0}}`;

  return {
    ...makeQcm(choix, correctStr, texte, { ...OPT, label: 'Placer des fractions de dénominateurs différents (PPCM)' }),
    elements_additionnels: {
      version: 1,
      elements: [
        { type: 'numberline', config: nlConfig },
        { type: 'expression', config: { contenu: `\\dfrac{${a1}}{${b1}}` } },
        { type: 'expression', config: { contenu: `\\dfrac{${a2}}{${b2}}` } },
      ],
    },
    _meta: { generatorKey: 'placer_den_melanges_qcm' }
  };
}

// ─── Export ───────────────────────────────────────────────────

const GENERATORS = {
  placer_fraction_qcm,
  placer_fraction_interactif,
  placer_fraction_relier,
  placer_fraction_vf,
  placer_methode_drag,
  placer_encadrement_saisie,
  placer_decomposition_saisie,
  placer_den_melanges_qcm
};

const FORMAT_LABELS = {
    qcm: 'QCM', interactif: 'Interactif', relier: 'Relier',
    vf: 'Vrai/Faux', drag: 'Drag', saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'placer_fraction', label: 'Placer une fraction sur une demi-droite graduée', formatsList: buildFormats('placer_fraction') },
    { id: 'placer_methode', label: 'Méthode de placement d\'une fraction', formatsList: buildFormats('placer_methode') },
    { id: 'placer_encadrement', label: 'Encadrement d\'une fraction par entiers', formatsList: buildFormats('placer_encadrement') },
    { id: 'placer_decomposition', label: 'Décomposition d\'une fraction pour placer', formatsList: buildFormats('placer_decomposition') },
    { id: 'placer_den_melanges', label: 'Fractions de dénominateurs différents (PPCM)', formatsList: buildFormats('placer_den_melanges') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : placer et encadrer se font en comptant les
    // graduations (même arbitrage que 620-622, revue 2026-08-02)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};
